import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { FUENTE, useTema } from './tema';
import { verLoQueSeFirma, firmarActa, urlDelActaEnPdf } from '../../services/minutasV2Service';
import TomarFirma from './TomarFirma';
import VisorDePdf from './VisorDePdf';

/**
 * FirmarActa — la pantalla a la que lleva el enlace del correo.
 *
 * Quien llega aquí puede no haber abierto nunca esta aplicación: le mandaron un
 * correo, pulsó un botón y está mirando algo que no conoce. Todo lo de abajo
 * sale de ahí.
 *
 * ── Qué firma, antes de firmar ───────────────────────────────────────────
 *
 * Lo primero que se enseña es el CONTENIDO del acta —quién fue, qué se acordó,
 * quién se lleva cada compromiso—, no un botón junto al nombre de un archivo.
 * Una pantalla que dice «firma el acta 0001/2026» y ofrece un botón verde
 * consigue que la gente firme sin leer, y entonces la firma no significa nada.
 * El documento entero está a un clic, para quien quiera verlo tal cual: el
 * mismo PDF que le llegó adjunto al correo, no el .docx vivo de SharePoint.
 * Enlazar al archivo vivo dejaría que lo que se lee aquí cambiara a media ronda
 * —y que el tercer firmante viera algo distinto de lo que firmó el primero—,
 * además de exigirle permisos en el sitio a quien quizá solo entra a firmar.
 *
 * ── Rechazar cuesta lo mismo que aprobar ─────────────────────────────────
 *
 * Los dos botones se ven igual de disponibles. Esconder el de rechazar detrás
 * de un enlace pequeño —que es la tentación— empuja a aprobar, y el sentido de
 * pedir una firma es que la respuesta pueda ser que no.
 *
 * Eso sí: rechazar obliga a explicar por qué. Un «no» sin motivos deja a quien
 * tiene que corregir el acta sin saber qué corregir, y la ronda entera se
 * repite para nada.
 *
 * ── El enlace no es la credencial ────────────────────────────────────────
 *
 * El token dice QUÉ se firma. Quién firma lo dice la sesión: hay que entrar con
 * la cuenta de la organización y el servidor comprueba que el correo coincida
 * con el del firmante. Un correo reenviado —a propósito o por error— no
 * convierte a nadie en firmante.
 */

const estilos = (C) => ({
  raiz: {
    minHeight: '100vh', background: C.fondoHondo, color: C.texto,
    fontFamily: FUENTE, padding: '28px 16px'
  },
  hoja: {
    maxWidth: 760, margin: '0 auto', background: C.fondo,
    border: `1px solid ${C.borde}`, borderRadius: 13, overflow: 'hidden'
  },
  cabecera: { padding: '22px 26px 18px', borderBottom: `1px solid ${C.borde}` },
  sobretitulo: {
    fontSize: 11.5, fontWeight: 600, letterSpacing: .6, textTransform: 'uppercase',
    color: C.tenue, marginBottom: 7
  },
  titulo: { margin: 0, fontSize: 21, fontWeight: 600, lineHeight: 1.3 },
  meta: { marginTop: 9, fontSize: 13, color: C.suave, lineHeight: 1.7 },
  cuerpo: { padding: '20px 26px' },
  seccion: { marginBottom: 22 },
  rotulo: {
    fontSize: 11.5, fontWeight: 600, letterSpacing: .5, textTransform: 'uppercase',
    color: C.tenue, marginBottom: 9
  },
  tabla: { width: '100%', borderCollapse: 'collapse', fontSize: 13.5 },
  th: {
    textAlign: 'left', padding: '7px 10px', fontSize: 11.5, fontWeight: 600,
    color: C.tenue, borderBottom: `1px solid ${C.borde}`, background: C.panelAlto
  },
  td: {
    padding: '8px 10px', borderBottom: `1px solid ${C.borde}`,
    verticalAlign: 'top', lineHeight: 1.55
  },
  parrafo: { fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap', margin: 0, color: C.texto },
  pie: {
    padding: '18px 26px', borderTop: `1px solid ${C.borde}`, background: C.panelAlto,
    display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center'
  },
  boton: {
    display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 22px',
    borderRadius: 8, fontSize: 14.5, fontWeight: 600, fontFamily: FUENTE,
    cursor: 'pointer', border: `1px solid ${C.borde}`, background: C.panel, color: C.texto
  },
  botonSi: { background: C.acento, borderColor: C.acento, color: C.acentoTexto },
  botonNo: { borderColor: C.error, color: C.error },
  enlace: { color: C.acentoClaro, fontSize: 13 },
  // El editor de observaciones.
  editor: {
    minHeight: 160, maxHeight: 420, overflowY: 'auto', padding: '12px 14px',
    border: `1px solid ${C.borde}`, borderRadius: 8, background: C.panel,
    fontSize: 14, lineHeight: 1.65, outline: 'none'
  },
  barra: { display: 'flex', gap: 5, marginBottom: 8, flexWrap: 'wrap' },
  herramienta: {
    padding: '5px 10px', borderRadius: 6, border: `1px solid ${C.borde}`,
    background: C.panelAlto, color: C.texto, fontSize: 13, cursor: 'pointer',
    fontFamily: FUENTE, minWidth: 32
  },
  aviso: (malo) => ({
    padding: '11px 14px', borderRadius: 8, fontSize: 13.5, lineHeight: 1.6,
    background: malo ? C.errorFondo || 'rgba(220,60,60,.10)' : C.panelAlto,
    border: `1px solid ${malo ? C.error : C.borde}`,
    color: malo ? C.error : C.suave, marginBottom: 16
  }),
  centrado: {
    maxWidth: 520, margin: '12vh auto', textAlign: 'center',
    fontSize: 15, lineHeight: 1.7, color: C.suave
  },
  sello: {
    display: 'inline-block', padding: '3px 10px', borderRadius: 20,
    fontSize: 12, fontWeight: 600
  }
});

const COMO_SE_LLAMA = {
  pendiente: 'Aún no te toca',
  avisado: 'Esperando tu respuesta',
  aprobada: 'Ya la firmaste',
  rechazada: 'La rechazaste',
  cancelada: 'Se canceló'
};

/** Una fecha en cristiano, sin la hora cuando no aporta. */
function dia(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('es', {
    day: 'numeric', month: 'long', year: 'numeric'
  });
}

const FirmarActa = ({ token }) => {
  const { C } = useTema();
  const S = useMemo(() => estilos(C), [C]);
  const { instance, accounts } = useMsal();
  const entrado = useIsAuthenticated();
  const cuenta = accounts && accounts[0];

  const miCorreo = (cuenta && (cuenta.username || (cuenta.idTokenClaims
    && (cuenta.idTokenClaims.preferred_username || cuenta.idTokenClaims.email)))) || '';

  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState(null);
  const [rechazando, setRechazando] = useState(false);
  const [mandando, setMandando] = useState(false);

  // La rubrica dibujada. Hace falta para aprobar: el lienzo y el boton estaban
  // juntos pero sueltos, y se podia pulsar «Aceptar y firmar» sin dibujar nada.
  // Una pantalla que pide algo y luego no lo necesita enseña que lo que pide no
  // importa, y la proxima vez nadie lo mira.
  const [rubrica, setRubrica] = useState(null);
  const [hecho, setHecho] = useState(null);
  const editorRef = useRef(null);

  // ── Lo que se va a firmar ───────────────────────────────────────────
  //
  // Se pide SIN sesión: quien llega puede no haber entrado todavía, y
  // obligarle a autenticarse antes de poder siquiera ver de qué va el asunto
  // es pedirle credenciales a ciegas. Firmar sí la exige.
  useEffect(() => {
    let vigente = true;
    (async () => {
      try {
        const s = await verLoQueSeFirma(token);
        if (vigente) setDatos(s);
      } catch (e) {
        if (vigente) setFallo(e.message || 'No se pudo leer este enlace.');
      } finally {
        if (vigente) setCargando(false);
      }
    })();
    return () => { vigente = false; };
  }, [token]);

  const entrar = useCallback(() => {
    instance.loginRedirect(loginRequest).catch(() => {
      setFallo('No se pudo iniciar sesión. Prueba otra vez.');
    });
  }, [instance]);

  /**
   * Pegar una imagen.
   *
   * Se interceptan las imágenes del portapapeles y se meten como `data:` en el
   * propio HTML. Es lo que la gente hace —una captura de la cifra que está
   * mal— y sin esto el navegador pegaría una referencia a un archivo local que
   * nadie más podría abrir.
   *
   * El resto del pegado se deja pasar como texto plano: el HTML que viene de
   * Word o de una página trae estilos que no significan nada aquí y que el
   * servidor va a tirar de todas formas.
   */
  const alPegar = useCallback((e) => {
    const items = (e.clipboardData && e.clipboardData.items) || [];
    for (const item of items) {
      if (item.type && item.type.startsWith('image/')) {
        e.preventDefault();
        const archivo = item.getAsFile();
        if (!archivo) return;
        if (archivo.size > 2 * 1024 * 1024) {
          setFallo('Esa imagen pesa más de 2 MB. Recórtala o baja su calidad.');
          return;
        }
        const lector = new FileReader();
        lector.onload = () => {
          document.execCommand('insertHTML', false,
            `<img src="${lector.result}" style="max-width:100%;height:auto" />`);
        };
        lector.readAsDataURL(archivo);
        return;
      }
    }
    // Texto: sin el formato de donde venga.
    const texto = e.clipboardData && e.clipboardData.getData('text/plain');
    if (texto) {
      e.preventDefault();
      document.execCommand('insertText', false, texto);
    }
  }, []);

  const formato = useCallback((orden) => {
    document.execCommand(orden, false, null);
    if (editorRef.current) editorRef.current.focus();
  }, []);

  const decidir = useCallback(async (aprueba) => {
    if (mandando) return;
    const observaciones = editorRef.current ? editorRef.current.innerHTML : '';

    // ── Sin rúbrica no se aprueba ────────────────────────────────────────
    //
    // El lienzo y el botón estaban juntos pero sueltos: se podía pulsar
    // «Aceptar y firmar» sin dibujar nada y el acta quedaba firmada igual. La
    // pantalla pedía una firma y luego no la necesitaba, que es la peor
    // combinación: enseña que lo que pide no importa.
    //
    // Se exige al aprobar y no al rechazar, porque rechazar no es firmar: ahí
    // lo que hace falta son los motivos, y eso ya se exige abajo.
    if (aprueba && !rubrica) {
      setFallo('Pon tu firma antes de aceptar —dibújala, escríbela o sube una '
        + 'imagen—: es la que aparecerá en tu fila del acta.');
      return;
    }

    if (!aprueba) {
      const soloTexto = editorRef.current ? editorRef.current.innerText.trim() : '';
      const hayImagen = /<img/i.test(observaciones);
      if (!soloTexto && !hayImagen) {
        setFallo('Para rechazarla hace falta explicar por qué: quien la corrija necesita saberlo.');
        return;
      }
    }

    setMandando(true);
    setFallo(null);
    try {
      const r = await firmarActa(token, {
        aprueba,
        observaciones: aprueba ? null : observaciones,
        // La rubrica solo acompaña a una aprobacion. Mandarla con un rechazo
        // seria guardar una firma de alguien que dijo que no.
        firma: aprueba ? rubrica : null,
        comoCorreo: miCorreo
      });
      setHecho({
        aprueba,
        yaEstaba: r.alreadyDone,
        cerrada: r.closed
      });
    } catch (e) {
      setFallo(e.message || 'No se pudo registrar tu respuesta.');
    } finally {
      setMandando(false);
    }
  }, [mandando, token, miCorreo, rubrica]);

  if (cargando) {
    return <div style={S.raiz}><p style={S.centrado}>Abriendo el acta…</p></div>;
  }

  if (fallo && !datos) {
    return (
      <div style={S.raiz}>
        <div style={S.centrado}>
          <p>{fallo}</p>
          <p style={{ fontSize: 13, color: C.tenue }}>
            Si el acta sigue pendiente de tu firma, pídele a quien te la mandó que te la
            reenvíe: los enlaces dejan de valer cuando la ronda se cierra o se cancela.
          </p>
        </div>
      </div>
    );
  }

  // ── Ya respondió ────────────────────────────────────────────────────
  if (hecho) {
    return (
      <div style={S.raiz}>
        <div style={{ ...S.hoja, padding: '34px 30px', textAlign: 'center' }}>
          <p style={{ fontSize: 17, fontWeight: 600, margin: '0 0 10px' }}>
            {hecho.yaEstaba ? 'Esto ya estaba respondido.'
              : hecho.aprueba ? 'Firmada. Gracias.' : 'Registrado: no la apruebas.'}
          </p>
          <p style={{ fontSize: 14, color: C.suave, lineHeight: 1.7, margin: 0 }}>
            {hecho.aprueba
              ? (
                <>
                  {/* Lo primero, lo que se acaba de hacer. Antes la pantalla
                      pasaba directamente a contar lo que ocurre DESPUÉS —a quién
                      se avisa, a quién le toca— y quien acababa de firmar se
                      quedaba sin la frase que confirma su propio acto. */}
                  <strong style={{ color: C.texto, fontWeight: 600 }}>
                    Has aceptado la minuta.
                  </strong>
                  <br />
                  {hecho.cerrada === 'aprobada'
                    ? 'Eras la última: el acta queda firmada por todos, y se les manda firmada a todos los implicados.'
                    : datos && datos.modo === 'paralelo'
                      ? 'Se ha avisado a quien la mandó. Ya no tienes que hacer nada más.'
                      : 'Se ha avisado a la siguiente persona. Ya no tienes que hacer nada más.'}
                </>
              )
              : 'Se le ha mandado tu explicación a quien redactó el acta para que la corrija. '
                + 'Cuando esté lista te llegará un correo nuevo: será un envío desde el principio.'}
          </p>
          <p style={{ fontSize: 12.5, color: C.tenue, marginTop: 20 }}>
            Puedes cerrar esta página.
          </p>
        </div>
      </div>
    );
  }

  const f = datos.firmante;

  // Los recuadros de la ronda, con el suyo marcado. Vienen del servidor ya con
  // `mio` puesto —lo decide él comparando el orden, no el navegador— y sin las
  // rúbricas de los demás: esas se ven estampadas en el PDF, que es donde
  // significan algo.
  const recuadros = Array.isArray(datos.recuadros) ? datos.recuadros : [];
  const misRecuadros = recuadros.filter(c => c.mio);

  const yaRespondio = f.estado === 'aprobada' || f.estado === 'rechazada';
  const rondaCerrada = datos.estadoRonda !== 'en curso';
  const noEsTuTurno = f.estado === 'pendiente' && datos.modo === 'secuencial';
  const otroUsuario = entrado && miCorreo
    && miCorreo.toLowerCase() !== String(f.correo || '').toLowerCase();

  const resumen = datos.resumen || {};

  return (
    <div style={S.raiz}>
      <div style={S.hoja}>
        <div style={S.cabecera}>
          <div style={S.sobretitulo}>Acta pendiente de tu firma</div>
          <h1 style={S.titulo}>{datos.titulo || 'Acta de reunión'}</h1>
          <div style={S.meta}>
            {datos.folio && <>Folio {datos.folio}<br /></>}
            {resumen.fecha && <>Reunión del {dia(resumen.fecha)}<br /></>}
            {datos.solicitante && <>La envía {datos.solicitante}<br /></>}
            {/* El puesto en la fila solo significa algo cuando hay fila. En
                paralelo le llega a todos a la vez y cualquiera puede firmar
                primero: decirle a alguien que es «el número 2» cuando puede
                firmar ya es confundirle con un dato que no manda nada. Paso de
                verdad —a quien le dijo que era la 2 firmó la primera—. */}
            {datos.modo === 'paralelo'
              ? `Se ha pedido la firma de ${datos.totalFirmantes || 'varias'} personas a la vez; una de ellas eres tú.`
              : `Te corresponde firmar en el lugar ${f.orden} del orden.`}
          </div>
        </div>

        <div style={S.cuerpo}>
          {yaRespondio && (
            <div style={S.aviso(false)}>
              <strong>{COMO_SE_LLAMA[f.estado]}</strong>
              {f.decididoEl ? ` el ${dia(f.decididoEl)}.` : '.'}
              {f.estado === 'rechazada' && ' Lo que dijiste sigue abajo.'}
            </div>
          )}

          {!yaRespondio && rondaCerrada && (
            <div style={S.aviso(true)}>
              Esta ronda de firmas ya se cerró, así que no hace falta que hagas nada.
              Si el acta se corrige, recibirás un correo nuevo.
            </div>
          )}

          {!yaRespondio && !rondaCerrada && noEsTuTurno && (
            <div style={S.aviso(false)}>
              Todavía no es tu turno: faltan firmas antes que la tuya. Puedes leer el acta
              ahora, pero el botón se activará cuando te llegue tu correo.
            </div>
          )}

          {otroUsuario && (
            <div style={S.aviso(true)}>
              Este enlace es para <strong>{f.correo}</strong> y has entrado como{' '}
              <strong>{miCorreo}</strong>. Si la firma te corresponde, pídele a quien mandó
              el acta que te la asigne a ti.
            </div>
          )}

          {/* El recado de quien la mandó. Va arriba del todo, antes del acta:
              es contexto para decidir —«esta es la versión corregida», «fíjate
              en el punto 4»— y debajo del resumen llegaría tarde.

              Atribuido, porque es lo único de esta pantalla que no escribió la
              aplicación: sin decir de quién es, se lee como una instrucción
              del sistema. */}
          {datos.mensaje && (
            <div style={{ ...S.seccion, borderLeft: `3px solid ${C.acento}`, paddingLeft: 14 }}>
              <div style={S.rotulo}>
                Mensaje de {datos.solicitante || 'quien te la mandó'}
              </div>
              <p style={{ ...S.parrafo, whiteSpace: 'pre-wrap' }}>{datos.mensaje}</p>
            </div>
          )}

          {/* ── Lo que se firma ────────────────────────────────────── */}
          {resumen.objetivo && (
            <div style={S.seccion}>
              <div style={S.rotulo}>Objetivo de la reunión</div>
              <p style={S.parrafo}>{resumen.objetivo}</p>
            </div>
          )}

          {Array.isArray(resumen.asistentes) && resumen.asistentes.length > 0 && (
            <div style={S.seccion}>
              <div style={S.rotulo}>Quiénes asistieron</div>
              <p style={S.parrafo}>{resumen.asistentes.join(' · ')}</p>
            </div>
          )}

          {Array.isArray(resumen.acuerdos) && resumen.acuerdos.length > 0 && (
            <div style={S.seccion}>
              <div style={S.rotulo}>Acuerdos y compromisos</div>
              <table style={S.tabla}>
                <thead>
                  <tr>
                    <th style={S.th}>Acuerdo</th>
                    <th style={{ ...S.th, width: 160 }}>Responsable</th>
                    <th style={{ ...S.th, width: 130 }}>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {resumen.acuerdos.map((a, i) => (
                    <tr key={i}>
                      <td style={S.td}>{a.texto}</td>
                      <td style={S.td}>{a.responsable || '—'}</td>
                      <td style={S.td}>{a.fecha || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!resumen.objetivo && !(resumen.acuerdos || []).length && (
            <div style={S.aviso(false)}>
              No hay un resumen guardado de esta acta. Abre el documento para leerla antes
              de decidir: no firmes algo que no has visto.
            </div>
          )}

          {/* El PDF que se congeló al abrir la ronda: lo mismo que le llegó
              adjunto en el correo. Si esa ronda no lo tiene —las abiertas antes
              de que el acta viajara en PDF— queda el enlace al documento, que
              es peor pero es algo que leer. */}
          {(datos.hayPdf || datos.documento) && (
            <div style={S.seccion}>
              <a
                href={datos.hayPdf ? urlDelActaEnPdf(token) : datos.documento}
                target="_blank" rel="noopener noreferrer" style={S.enlace}
              >
                {datos.hayPdf ? 'Leer el acta completa (PDF) ↗' : 'Abrir el documento completo ↗'}
              </a>
            </div>
          )}

          {/* ── El documento, aquí mismo ─────────────────────────────────
              Un enlace que abre otra pestaña saca a la persona de la pantalla
              donde está decidiendo, y lo que vuelve es un PDF sin contexto: ni
              dónde le toca firmar, ni cómo va la ronda.

              Aquí se navega el acta con sus recuadros encima: el suyo marcado,
              los de los demás en gris, y los que ya firmaron con la rúbrica
              estampada —que es lo que se pidió: si eres el último, ves las
              firmas anteriores en el propio documento—.

              El estampado lo hace el servidor al servir el PDF, partiendo
              siempre del limpio. Ver `FirmaEnPdf.js`. */}
          {datos.hayPdf && (
            <div style={S.seccion}>
              <div style={S.rotulo}>El documento</div>
              {misRecuadros.length === 0 && recuadros.length > 0 && (
                <div style={{ fontSize: 12.5, color: C.tenue, lineHeight: 1.6, marginBottom: 8 }}>
                  Quien te la mandó no señaló un sitio para tu firma, así que tu rúbrica irá a
                  la tabla de asistentes del acta.
                </div>
              )}
              <VisorDePdf
                url={urlDelActaEnPdf(token)}
                recuadros={recuadros}
                // Directo a su hoja. En un acta de ocho hojas, buscar el propio
                // recuadro pasándolas una a una es trabajo que no tiene por qué
                // hacer quien solo viene a firmar.
                paginaInicial={misRecuadros.length > 0 ? misRecuadros[0].pagina : 1}
                pista={misRecuadros.length > 0
                  ? 'Tu firma va en el recuadro azul. Las que ya están firmadas se ven con su rúbrica.'
                  : null}
              />
            </div>
          )}

          {/* Lo que dijo al rechazar, si ya lo hizo. */}
          {f.estado === 'rechazada' && f.observaciones && (
            <div style={S.seccion}>
              <div style={S.rotulo}>Lo que explicaste</div>
              <div
                style={{ ...S.editor, minHeight: 0 }}
                dangerouslySetInnerHTML={{ __html: f.observaciones }}
              />
            </div>
          )}

          {/* ── El editor de observaciones ─────────────────────────── */}
          {rechazando && !yaRespondio && (
            <div style={S.seccion}>
              <div style={S.rotulo}>Por qué no la apruebas</div>
              <div style={S.barra}>
                <button type="button" style={{ ...S.herramienta, fontWeight: 700 }}
                  onClick={() => formato('bold')} title="Negrita">B</button>
                <button type="button" style={{ ...S.herramienta, fontStyle: 'italic' }}
                  onClick={() => formato('italic')} title="Cursiva">I</button>
                <button type="button" style={{ ...S.herramienta, textDecoration: 'underline' }}
                  onClick={() => formato('underline')} title="Subrayado">U</button>
                <button type="button" style={S.herramienta}
                  onClick={() => formato('insertUnorderedList')} title="Lista">• Lista</button>
              </div>
              <div
                ref={editorRef}
                style={S.editor}
                contentEditable
                suppressContentEditableWarning
                onPaste={alPegar}
                data-marcador="Explica qué está mal y qué habría que corregir."
              />
              <p style={{ fontSize: 12.5, color: C.tenue, marginTop: 7, lineHeight: 1.6 }}>
                Puedes pegar capturas de pantalla directamente (Ctrl+V). Quien tenga que
                corregir el acta va a leer esto, así que cuanto más concreto, menos vueltas.
              </p>
            </div>
          )}

          {/* ── Tu firma, dibujada ──────────────────────────────────
              Va aquí arriba y no en el pie, junto a los botones: el pie es
              para decidir, y esto es parte de lo que se revisa antes de
              decidir. Solo cuando toca firmar de verdad —entrado, en tu turno,
              y no mientras se redacta un rechazo—, porque en los demás casos
              sería un lienzo que no se puede usar o que no significa nada. */}
          {!yaRespondio && !rondaCerrada && entrado && !otroUsuario && !noEsTuTurno && !rechazando && (
            <div style={{ marginTop: 20 }}>
              <h3 style={{ margin: '0 0 2px', fontSize: 14.5, fontWeight: 600, color: C.texto }}>
                Tu firma
              </h3>
              <p style={{ margin: '0 0 8px', fontSize: 12.5, color: C.tenue, lineHeight: 1.6 }}>
                Dibújala, escríbela o sube una imagen: se estampará en tu fila de la
                tabla de asistentes del acta.
              </p>
              <TomarFirma
                onCambio={setRubrica}
                nombre={(f && f.nombre) || ''}
                correo={miCorreo}
                desactivado={mandando}
              />
            </div>
          )}

          {fallo && <div style={S.aviso(true)}>{fallo}</div>}
        </div>

        {/* ── Decidir ────────────────────────────────────────────── */}
        {!yaRespondio && !rondaCerrada && (
          <div style={S.pie}>
            {!entrado ? (
              <>
                <button style={{ ...S.boton, ...S.botonSi }} onClick={entrar}>
                  Entrar para firmar
                </button>
                <span style={{ fontSize: 12.5, color: C.tenue, lineHeight: 1.6 }}>
                  Hay que entrar con tu cuenta: el enlace dice qué se firma, tu sesión dice
                  quién firma.
                </span>
              </>
            ) : rechazando ? (
              <>
                <button
                  style={{ ...S.boton, ...S.botonNo }}
                  onClick={() => decidir(false)}
                  disabled={mandando || otroUsuario}
                >
                  {mandando ? 'Enviando…' : 'Rechazar y enviar mis observaciones'}
                </button>
                <button style={S.boton} onClick={() => { setRechazando(false); setFallo(null); }}>
                  Volver
                </button>
              </>
            ) : (
              <>
                {/* Los dos, igual de visibles. Ver la cabecera del archivo. */}
                <button
                  style={{
                    ...S.boton, ...S.botonSi,
                    // Apagado a la vista mientras no haya trazo, pero NO
                    // `disabled`: un boton que no responde no se distingue de
                    // uno roto. Se pulsa, y entonces dice que falta la firma.
                    opacity: (!rubrica && !mandando) ? .55 : 1
                  }}
                  onClick={() => decidir(true)}
                  disabled={mandando || otroUsuario || noEsTuTurno}
                  title={!rubrica ? 'Primero pon tu firma, en el recuadro de arriba' : ''}
                >
                  {mandando ? 'Firmando…' : 'Estoy de acuerdo, firmar'}
                </button>
                <button
                  style={{ ...S.boton, ...S.botonNo }}
                  onClick={() => { setRechazando(true); setFallo(null); }}
                  disabled={mandando || otroUsuario || noEsTuTurno}
                >
                  No estoy de acuerdo
                </button>
                {entrado && !otroUsuario && (
                  <span style={{ fontSize: 12.5, color: C.tenue, marginLeft: 'auto' }}>
                    Firmas como {miCorreo}
                  </span>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FirmarActa;
